import React, { useEffect, useMemo, useState } from "react";
import { Sidebar } from "../components/Note";
import UserMenu from "../components/UserMenu";
import api from "../api";
import {
  FiUser,
  FiCreditCard,
  FiChevronRight,
  FiSun,
  FiMoon,
  FiArrowLeft,
  FiSettings,
  FiClock,
  FiGlobe,
} from "react-icons/fi";
import { isPaid } from "../utils/payments";
import {
  getUserTimezone,
  ensureUserTimezone,
  formatClockInTimezone,
  getTimezoneMeta,
  setUserTimezone,
  TIMEZONE_OPTIONS,
} from "../utils/timezone";

export default function Configuracion() {
  // 🌙 Tema oscuro
  const [dark, setDark] = useState(localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  // 🔁 Vista: index | datos | planes
  const [view, setView] = useState("index");
  const [tab, setTab] = useState("datos");

  // 👤 Datos usuario
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // 🔐 Cambio de clave
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [cfgPwd, setCfgPwd] = useState("");
  const [changing, setChanging] = useState(false);
  const [activeTimezone, setActiveTimezone] = useState(() => getUserTimezone());
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [timezoneNotice, setTimezoneNotice] = useState("");
  const timezoneMeta = useMemo(() => getTimezoneMeta(activeTimezone), [activeTimezone]);
  const timezoneClock = useMemo(() => formatClockInTimezone(new Date(), activeTimezone), [activeTimezone]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("user/profile/");
        setName(data?.name || "");
        setEmail(data?.email || "");
        setPhone(data?.phone || "");
        if (data?.timezone) {
          const tz = setUserTimezone(String(data.timezone));
          setActiveTimezone(tz);
        } else {
          setActiveTimezone(getUserTimezone());
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "No se pudo cargar tu perfil");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveTimezonePreference = async () => {
    setSavingTimezone(true);
    setTimezoneNotice("");
    try {
      const tz = setUserTimezone(activeTimezone);
      setActiveTimezone(tz);
      try {
        await api.patch("user/profile/", { timezone: tz });
      } catch (_) {
        // El backend puede no tener el campo aun; la preferencia local sigue activa.
      }
      setTimezoneNotice(`Zona horaria activa: ${getTimezoneMeta(tz).label}`);
    } catch (e) {
      setTimezoneNotice(e?.message || "No se pudo guardar la zona horaria");
    } finally {
      setSavingTimezone(false);
    }
  };

  // 💾 Guardar perfil
  const saveProfile = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch("user/profile/", { name, email, phone });
      alert("Datos actualizados");
    } catch (e) {
      const msg = e?.response?.data?.errors
        ? JSON.stringify(e.response.data.errors)
        : e?.response?.data?.detail || e?.message || "No se pudo guardar";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // 🔑 Cambiar contraseña
  const changePassword = async () => {
    setChanging(true);
    setError("");
    try {
      if (!curPwd || !newPwd || !cfgPwd) {
        setError("Completa los tres campos de contraseña");
        return;
      }
      if (newPwd.length < 6) {
        setError("La nueva clave debe tener al menos 6 caracteres");
        return;
      }
      if (newPwd !== cfgPwd) {
        setError("Las claves no coinciden");
        return;
      }
      await api.post("user/change-password/", {
        current_password: curPwd,
        new_password: newPwd,
        confirm_password: cfgPwd,
      });
      alert("Contraseña cambiada");
      setCurPwd("");
      setNewPwd("");
      setCfgPwd("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo cambiar la clave");
    } finally {
      setChanging(false);
    }
  };

  // 🧭 Header dinámico
  const Header = () => {
    let icon = <FiSettings />;
    let title = "Configuración";
    let subtitle = "Administra tu cuenta y preferencias.";

    if (view === "datos") {
      icon = <FiUser />;
      title = "Mis datos";
      subtitle = "Edita tu información personal y contraseña.";
    } else if (view === "planes") {
      icon = <FiCreditCard />;
      title = "Planes de pago";
      subtitle = "Consulta o actualiza tus planes en WaveSales.";
    } else if (view === "horario") {
      icon = <FiClock />;
      title = "Zona horaria";
      subtitle = "Define el horario oficial para campañas y envíos del sistema.";
    }

    return (
      <div
        className={`rounded-2xl shadow-md p-6 mb-8 border transition-colors duration-500
          ${dark ? "bg-[#0f1a2d] border-[#1f3048]" : "bg-white border-[#d3dfef]"}`}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="flex items-start gap-4">
            <div
              className="flex items-center justify-center rounded-xl w-16 h-16 shadow-md"
              style={{
                background: dark
                  ? "linear-gradient(135deg,#1d4ed8,#0ea5e9)"
                  : "linear-gradient(135deg,#0ea5e9,#22c55e)",
                color: "#fff",
                fontSize: "1.5rem",
              }}
            >
              {icon}
            </div>

            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
              <p className="text-sm text-gray-700 dark:text-[#9cb0c8]">{subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setDark(!dark)}
              className={`px-4 py-2 rounded-xl text-sm font-medium shadow-md transition-all ${
                dark
                  ? "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900"
                  : "bg-gradient-to-r from-[#0f62fe] to-[#00a3a3] text-white"
              }`}
            >
              {dark ? "☀ Claro" : "🌙 Oscuro"}
            </button>

            <UserMenu usernameProp={localStorage.getItem("username")} />
          </div>
        </div>
      </div>
    );
  };

  // 🪪 Tarjetas menú principal
  const CardItem = ({ icon, title, subtitle, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center justify-between gap-4 px-5 py-5 rounded-2xl shadow-md border transition-transform hover:scale-[1.02] ${
        dark ? "bg-[#0f1a2d] border-[#1f3048]" : "bg-white border-[#d3dfef]"
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-sm ${
            dark ? "bg-[#111c2c] text-gray-100" : "bg-[#eef4ff] text-[#24517e]"
          }`}
        >
          {icon}
        </div>
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100">{title}</div>
          <div className="text-sm text-gray-700 dark:text-gray-400">{subtitle}</div>
        </div>
      </div>
      <FiChevronRight className="text-gray-400" />
    </button>
  );

  // 🏠 Vista principal
  const IndexView = () => (
    <>
      <Header />
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CardItem
            icon={<FiUser />}
            title="Mis datos"
            subtitle="Edita tu perfil y contraseña"
            onClick={() => setView("datos")}
          />
          <CardItem
            icon={<FiCreditCard />}
            title="Planes de pago"
            subtitle="Consulta o cambia tu plan"
            onClick={() => setView("planes")}
          />
          <CardItem
            icon={<FiGlobe />}
            title="Zona horaria de envíos"
            subtitle="Define el horario oficial para campañas"
            onClick={() => setView("horario")}
          />
        </div>
      </div>
    </>
  );

  // 🔙 Botón volver
  const BackHeader = () => (
    <div
      className={`rounded-2xl shadow-md p-4 border mb-6 flex justify-between items-center ${
        dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <button
        onClick={() => setView("index")}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
      >
        <FiArrowLeft /> Volver a mi cuenta
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#eef3fb] dark:bg-[#060f1f] transition-colors duration-500">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8">
        {view === "index" && <IndexView />}

        {view !== "index" && (
          <>
            <Header />
            <BackHeader />

            {view === "datos" && (
              <div
                className={`rounded-2xl shadow-md border p-6 transition-colors duration-500 ${
                  dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Mis datos</h2>
                {error && <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>}
                {loading ? (
                  <div className="text-gray-700 dark:text-gray-400">Cargando…</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Nombre</span>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Correo</span>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Teléfono</span>
                        <input
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={saveProfile}
                        disabled={saving}
                        className="px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 disabled:opacity-60"
                      >
                        {saving ? "Guardando…" : "Guardar"}
                      </button>
                    </div>

                    <hr className="my-6 border-gray-300 dark:border-gray-700" />

                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Cambio de clave</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Clave actual</span>
                        <input
                          type="password"
                          value={curPwd}
                          onChange={(e) => setCurPwd(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Nueva clave</span>
                        <input
                          type="password"
                          value={newPwd}
                          onChange={(e) => setNewPwd(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-sm text-gray-900 dark:text-gray-300">Confirmar clave</span>
                        <input
                          type="password"
                          value={cfgPwd}
                          onChange={(e) => setCfgPwd(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                      </label>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={changePassword}
                        disabled={changing}
                        className="px-5 py-2 rounded-lg bg-gray-800 text-white hover:bg-black active:scale-95 disabled:opacity-60"
                      >
                        {changing ? "Guardando…" : "Cambiar clave"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {view === "planes" && (
              <div
                className={`rounded-2xl shadow-md border p-6 transition-colors duration-500 ${
                  dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Planes de pago</h2>

                {(() => {
                  // Leer bandera de pago guardada por la landing / PayPal
                  let paidInfo = null;
                  try { paidInfo = JSON.parse(localStorage.getItem('ws_paid') || 'null'); } catch {}

                  const PLAN_LABEL = {
                    basic: 'Plan Básico',
                    pro: 'Plan Pro',
                    enterprise: 'Plan Enterprise',
                  };
                  const PLAN_FEATURES = {
                    basic: ['Leads ilimitados', 'Automatización avanzada', 'Soporte por email'],
                    pro: ['Leads ilimitados', 'Automatización avanzada', 'Soporte prioritario'],
                    enterprise: ['Todo incluido', 'Automatización avanzada', 'Soporte 24/7'],
                  };
                  const PLAN_DAYS = {
                    basic: 7,
                    pro: 30,
                    enterprise: 90,
                  };
                  const PLAN_PRICE = { basic: 7, pro: 29, enterprise: 69 };
                  const PLAN_PERIOD = { basic: '7días', pro: 'mes', enterprise: 'trimestral' };

                  if (!isPaid() || !paidInfo || !paidInfo.ts) {
                    return (
                      <div className={`rounded-xl p-4 border ${dark ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                        <div className="font-medium mb-1">Sin plan activo</div>
                        <div className="text-sm opacity-80">Aún no registramos un pago asociado a tu cuenta. Puedes seleccionar un plan en la landing.</div>
                        <div className="mt-3">
                          <a href="/#pricing" className="inline-block px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Ver planes</a>
                        </div>
                      </div>
                    );
                  }

                  const planKey = (paidInfo.plan || 'pro').toLowerCase();
                  const planName = PLAN_LABEL[planKey] || 'Plan seleccionado';
                  const days = PLAN_DAYS[planKey] || 30;
                  const purchase = new Date(paidInfo.ts);
                  const end = new Date(purchase.getTime() + days * 24 * 60 * 60 * 1000);
                  const now = new Date();
                  const msRem = Math.max(0, end.getTime() - now.getTime());
                  const daysRem = Math.ceil(msRem / (24 * 60 * 60 * 1000));

                  const fmt = (d) => isNaN(d.getTime()) ? '-' : d.toLocaleDateString();

                  const price = PLAN_PRICE[planKey] || 0;
                  const period = PLAN_PERIOD[planKey] || '';

                  // Pie de texto estilo ejemplo
                  const footnote = planKey === 'basic'
                    ? `plan 7 días solo ($1 x día). total $${price}`
                    : (planKey === 'pro'
                        ? `plan mensual ($${price} x mes). total $${price}`
                        : `plan trimestral. total $${price}`);

                  return (
                    <div className="w-full flex justify-center">
                      <div className="rounded-2xl p-8 w-full max-w-sm bg-gray-900 text-gray-100 shadow-xl border border-gray-700">
                        {/* Título y precio centrados */}
                        <h3 className="text-center text-2xl font-semibold mb-2">{planName}</h3>
                        <div className="text-center text-gray-300 mb-6">${price}/{period}</div>

                        {/* Características centradas */}
                        <ul className="space-y-3 text-center mb-8">
                          {(PLAN_FEATURES[planKey] || []).map((f, i) => (
                            <li key={`feat-${i}`} className="text-sm">{f}</li>
                          ))}
                        </ul>

                        {/* Bloque inferior alineación como ejemplo (lado izquierdo) */}
                        <div className="text-sm">
                          <div className="text-left">Días restantes</div>
                          <div className="text-left mb-4 font-medium">{daysRem} días</div>
                          <div className="text-left">${price.toFixed(2)} USD</div>
                        </div>

                        {/* Línea final centrada con fechas */}
                        <div className="mt-6 text-center text-sm text-gray-300">
                          {footnote}
                        </div>
                        <div className="mt-2 text-center text-xs text-gray-400">
                          Compra: {fmt(purchase)} • Cierre: {fmt(end)}
                        </div>

                        {paidInfo?.ref && (
                          <div className="mt-2 text-center text-[11px] text-gray-500">Ref: {paidInfo.ref}</div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {view === "horario" && (
              <div
                className={`rounded-2xl shadow-md border p-6 transition-colors duration-500 ${
                  dark ? "bg-[#0f1a2d] border-[#1f3048]" : "bg-white border-[#d3dfef]"
                }`}
              >
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">Configurar zona horaria</h2>
                <p className="mb-5 text-sm text-gray-700 dark:text-[#9cb0c8]">
                  Esta zona se usará para programar campañas y unificar la referencia horaria del sitio. Valor por defecto: GYE Ecuador.
                </p>

                <div className={`mb-5 rounded-xl border p-4 ${dark ? 'border-[#274062] bg-[#111c2c]' : 'border-[#d3dfef] bg-[#f7fbff]'}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.08em] text-gray-500 dark:text-[#8fa1bd]">Zona activa</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{timezoneMeta.label}</p>
                    </div>
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-wide text-red-300">Reloj activo</p>
                      <p className="font-mono text-lg font-bold text-red-500">{timezoneClock}</p>
                    </div>
                  </div>
                </div>

                <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200">Seleccionar zona horaria</label>
                <select
                  value={activeTimezone}
                  onChange={(e) => setActiveTimezone(e.target.value)}
                  className="w-full rounded-lg border border-[#d3dfef] bg-white px-3 py-2.5 text-sm text-[#1f3555] outline-none focus:border-[#58a6ff] dark:border-[#29415f] dark:bg-[#111c2c] dark:text-[#e6edf3]"
                >
                  {TIMEZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={saveTimezonePreference}
                    disabled={savingTimezone}
                    className="rounded-lg bg-[#0f62fe] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0b4fd1] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingTimezone ? 'Guardando...' : 'Guardar zona horaria'}
                  </button>
                </div>

                {timezoneNotice && (
                  <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
                    {timezoneNotice}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
